import { useState } from 'react'

import { UseFormReturn } from 'react-hook-form'

interface UseBusinessCardOcrProps {
  form: UseFormReturn<Record<string, unknown>>
}

/**
 * Return type for useBusinessCardOcr hook
 */
interface UseBusinessCardOcrReturn {
  isProcessing: boolean
  onValidResult: (data: Record<string, string>) => void
  onProcessingStart: () => void
  onError: (error: string) => void
  aiFilledFields: Set<string>
  clearAiFilledFields: () => void
}

export function useBusinessCardOcr({
  form,
}: UseBusinessCardOcrProps): UseBusinessCardOcrReturn {
  const [isProcessing, setIsProcessing] = useState(false)
  const [aiFilledFields, setAiFilledFields] = useState<Set<string>>(new Set())

  function onValidResult(data: Record<string, string>): void {
    const currentValues = form.getValues()
    const newAiFilledFields = new Set<string>()

    const mergedData: Record<string, unknown> = { ...data }
    Object.keys(currentValues).forEach((key) => {
      const currentValue = currentValues[key]
      if (currentValue) {
        mergedData[key] = currentValue
      }
    })

    Object.keys(mergedData).forEach((key) => {
      if (mergedData[key] && currentValues[key] !== mergedData[key]) {
        form.setValue(key, mergedData[key])
        newAiFilledFields.add(key)
      }
    })

    // Update the set of AI-filled fields
    setAiFilledFields(newAiFilledFields)

    // Stop loading when processing is complete
    setIsProcessing(false)
  }

  function clearAiFilledFields(): void {
    setAiFilledFields(new Set())
  }

  function onProcessingStart(): void {
    // Clear previously highlighted fields when starting a new scan
    setAiFilledFields(new Set())
    setIsProcessing(true)
  }

  /**
   * Handle OCR errors and stop processing
   * @param error - Error message
   */
  function onError(error: string): void {
    // Stop loading when an error occurs
    setIsProcessing(false)
    console.error('Business card OCR error:', error)
  }

  return {
    isProcessing,
    onValidResult,
    onProcessingStart,
    onError,
    aiFilledFields,
    clearAiFilledFields,
  }
}
